import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchCurrentMatchday,
  fetchTeamCurrentMatchdayWindow,
  type BECurrentMatchday,
  type BECurrentMatchdayWindow,
  type CurrentMatchdayResponse,
} from '@/lib/network';

const CURRENT_MATCHDAY_QUERY_KEY_PREFIX = ['current-matchday'] as const;
const CURRENT_MATCHDAY_STALE_TIME_MS = 60 * 1000;

export const getCurrentMatchdayQueryKey = (
  tournamentCalendarId: string | undefined,
  source: 'public' | 'team' = 'public',
  userId?: string,
  format?: 'classic_season_long' | 'knockout'
) =>
  [
    ...CURRENT_MATCHDAY_QUERY_KEY_PREFIX,
    source,
    tournamentCalendarId ?? '',
    source === 'team' ? (userId ?? '') : '',
    format ?? '',
  ] as const;

export type UseCurrentMatchdayOptions = Omit<
  UseQueryOptions<CurrentMatchdayResponse, Error, BECurrentMatchday | null>,
  'queryKey' | 'queryFn'
>;

export type UseCurrentMatchdayWindowOptions = Omit<
  UseQueryOptions<CurrentMatchdayResponse, Error, BECurrentMatchdayWindow>,
  'queryKey' | 'queryFn'
>;

export const useCurrentMatchday = (
  tournamentCalendarId: string | undefined,
  options?: UseCurrentMatchdayOptions & {
    source?: 'public' | 'team';
    userId?: string;
    format?: 'classic_season_long' | 'knockout';
  }
): UseQueryResult<BECurrentMatchday | null, Error> => {
  const { enabled = true, source = 'public', userId, format, ...restOptions } = options ?? {};

  return useQuery<CurrentMatchdayResponse, Error, BECurrentMatchday | null>({
    queryKey: getCurrentMatchdayQueryKey(tournamentCalendarId, source, userId, format),
    queryFn: async () => {
      if (!tournamentCalendarId) {
        throw new Error('tournamentCalendarId is required to fetch current matchday');
      }
      return source === 'team'
        ? fetchTeamCurrentMatchdayWindow({ tournamentCalendarId, userId, format })
        : fetchCurrentMatchday({ tournamentCalendarId });
    },
    select: response => response.data.currentMatchday ?? null,
    enabled: enabled && !!tournamentCalendarId,
    staleTime: CURRENT_MATCHDAY_STALE_TIME_MS,
    ...restOptions,
  });
};

export const useCurrentMatchdayWindow = (
  tournamentCalendarId: string | undefined,
  options?: UseCurrentMatchdayWindowOptions & {
    source?: 'public' | 'team';
    userId?: string;
    format?: 'classic_season_long' | 'knockout';
  }
): UseQueryResult<BECurrentMatchdayWindow, Error> => {
  const { enabled = true, source = 'public', userId, format, ...restOptions } = options ?? {};

  return useQuery<CurrentMatchdayResponse, Error, BECurrentMatchdayWindow>({
    queryKey: getCurrentMatchdayQueryKey(tournamentCalendarId, source, userId, format),
    queryFn: async () => {
      if (!tournamentCalendarId) {
        throw new Error('tournamentCalendarId is required to fetch current matchday');
      }
      return source === 'team'
        ? fetchTeamCurrentMatchdayWindow({ tournamentCalendarId, userId, format })
        : fetchCurrentMatchday({ tournamentCalendarId });
    },
    select: response => response.data,
    enabled: enabled && !!tournamentCalendarId,
    staleTime: CURRENT_MATCHDAY_STALE_TIME_MS,
    ...restOptions,
  });
};
